import { useEffect, useState, useCallback, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { DateRangePicker, DateRangeValue } from '../ui/date-picker';
import { useNginxAnalytics } from '../../hooks/useNginxAnalytics';
import { SummaryKpiCards } from '../nginx-analytics/SummaryKpiCards';
import { SiteTabs } from '../nginx-analytics/SiteTabs';
import { NginxErrorBoundary } from '../nginx-analytics/ErrorBoundary';
import { KpiRow } from '../nginx-analytics/KpiRow';
import { HourlyTrafficChart } from '../nginx-analytics/HourlyTrafficChart';
import { MiniBarTable } from '../nginx-analytics/MiniBarTable';
import { HttpStatusTable } from '../nginx-analytics/HttpStatusTable';
import { TopVisitorsTable } from '../nginx-analytics/TopVisitorsTable';
import { ConversionFunnel } from '../nginx-analytics/ConversionFunnel';
import { AdsCampaignsTable } from '../nginx-analytics/AdsCampaignsTable';
import { CountryBreakdown } from '../nginx-analytics/CountryBreakdown';
import { ResponseTimeStats } from '../nginx-analytics/ResponseTimeStats';
import { ClientTypeBreakdown } from '../nginx-analytics/ClientTypeBreakdown';
import { RequestCategoryBreakdown } from '../nginx-analytics/RequestCategoryBreakdown';
import { SecurityOverview } from '../nginx-analytics/SecurityOverview';
import { RequestsTable } from '../nginx-analytics/RequestsTable';
import { TRAFFIC_CHANNEL_LABELS, DEVICE_TYPE_LABELS } from '../../constants/nginx-sites';
import { NginxSiteDetail } from '../../types';
import * as nginxAnalyticsService from '../../services/nginx-analytics.service';

interface NginxAnalyticsPageProps {
  accessToken: string | undefined;
}

function SiteDetailView({
  siteDetail,
  dateFrom,
  dateTo,
  accessToken,
}: {
  siteDetail: NginxSiteDetail;
  dateFrom: string;
  dateTo: string;
  accessToken: string;
}) {
  const trafficChannelData = siteDetail.trafficChannelBreakdown?.map((item) => ({
    ...item,
    name: TRAFFIC_CHANNEL_LABELS[item.name] || item.name,
  }));

  const deviceData = siteDetail.deviceBreakdown?.map((item) => ({
    ...item,
    name: DEVICE_TYPE_LABELS[item.name] || item.name,
  }));

  return (
    <Tabs defaultValue="visao-geral">
      <TabsList className="flex-wrap h-auto gap-1">
        <TabsTrigger value="visao-geral">Visão Geral</TabsTrigger>
        <TabsTrigger value="geografia">Geografia</TabsTrigger>
        <TabsTrigger value="audiencia">Audiência</TabsTrigger>
        <TabsTrigger value="trafego">Tráfego</TabsTrigger>
        <TabsTrigger value="performance">Performance</TabsTrigger>
        <TabsTrigger value="seguranca">Segurança</TabsTrigger>
        <TabsTrigger value="paginas">Páginas</TabsTrigger>
        <TabsTrigger value="visitantes">Visitantes</TabsTrigger>
      </TabsList>

      {/* Visão Geral: KPIs + Hourly Chart + Client Type + Request Categories */}
      <TabsContent value="visao-geral" className="space-y-4">
        <KpiRow site={siteDetail} />
        {siteDetail.hourlyBreakdown && siteDetail.hourlyBreakdown.length > 0 && (
          <HourlyTrafficChart data={siteDetail.hourlyBreakdown} color={siteDetail.color} />
        )}
        {(siteDetail.clientTypeBreakdown?.length || siteDetail.requestCategoryBreakdown?.length) ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {siteDetail.clientTypeBreakdown && siteDetail.clientTypeBreakdown.length > 0 && (
              <ClientTypeBreakdown data={siteDetail.clientTypeBreakdown} />
            )}
            {siteDetail.requestCategoryBreakdown && siteDetail.requestCategoryBreakdown.length > 0 && (
              <RequestCategoryBreakdown data={siteDetail.requestCategoryBreakdown} />
            )}
          </div>
        ) : null}
      </TabsContent>

      {/* Geografia: Country Breakdown */}
      <TabsContent value="geografia" className="space-y-4">
        {siteDetail.countryBreakdown && siteDetail.countryBreakdown.length > 0 ? (
          <CountryBreakdown data={siteDetail.countryBreakdown} />
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-muted-foreground">
                Dados de país não disponíveis para este site.
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Audiência: Devices + Browsers + OS */}
      <TabsContent value="audiencia">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {deviceData && deviceData.length > 0 && (
            <MiniBarTable title="Dispositivos" data={deviceData} color={siteDetail.color} />
          )}
          {siteDetail.browserBreakdown && siteDetail.browserBreakdown.length > 0 && (
            <MiniBarTable
              title="Navegadores"
              data={siteDetail.browserBreakdown}
              color={siteDetail.color}
            />
          )}
          {siteDetail.osBreakdown && siteDetail.osBreakdown.length > 0 && (
            <MiniBarTable
              title="Sistema Operacional"
              data={siteDetail.osBreakdown}
              color={siteDetail.color}
            />
          )}
        </div>
      </TabsContent>

      {/* Tráfego: Traffic Sources + Referrers + UTM Campaigns + HTTP Status */}
      <TabsContent value="trafego" className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {trafficChannelData && trafficChannelData.length > 0 && (
            <MiniBarTable
              title="Fontes de Tráfego"
              data={trafficChannelData}
              color={siteDetail.color}
            />
          )}
          {siteDetail.refererBreakdown && siteDetail.refererBreakdown.length > 0 && (
            <MiniBarTable
              title="Referências"
              data={siteDetail.refererBreakdown}
              color={siteDetail.color}
            />
          )}
        </div>

        {siteDetail.utmBreakdown && (
          <AdsCampaignsTable utmBreakdown={siteDetail.utmBreakdown} />
        )}

        {siteDetail.statusBreakdown && siteDetail.statusBreakdown.length > 0 && (
          <details className="group">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              <span>Status HTTP</span>
            </summary>
            <div className="mt-2">
              <HttpStatusTable data={siteDetail.statusBreakdown} />
            </div>
          </details>
        )}
      </TabsContent>

      {/* Performance: Response Time Stats */}
      <TabsContent value="performance" className="space-y-4">
        {siteDetail.responseTimeStats ? (
          <ResponseTimeStats data={siteDetail.responseTimeStats} />
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-muted-foreground">
                Dados de tempo de resposta não disponíveis para este site.
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Segurança: Security Overview */}
      <TabsContent value="seguranca" className="space-y-4">
        <SecurityOverview
          clientTypes={siteDetail.clientTypeBreakdown}
          sslProtocols={siteDetail.sslProtocolBreakdown}
          requestCategories={siteDetail.requestCategoryBreakdown}
        />
      </TabsContent>

      {/* Páginas: Unified RequestsTable */}
      <TabsContent value="paginas" className="space-y-4">
        <RequestsTable
          pages={siteDetail.topPages}
          apiEndpoints={siteDetail.topApiEndpoints}
          color={siteDetail.color}
        />
      </TabsContent>

      {/* Visitantes: Top Visitors + Conversion Funnel */}
      <TabsContent value="visitantes" className="space-y-4">
        <TopVisitorsTable
          siteId={siteDetail.siteId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          accessToken={accessToken}
        />
        {siteDetail.funnelData && siteDetail.funnelData.length > 0 && (
          <ConversionFunnel funnelData={siteDetail.funnelData} color={siteDetail.color} />
        )}
      </TabsContent>
    </Tabs>
  );
}

export function NginxAnalyticsPage({ accessToken }: NginxAnalyticsPageProps) {
  const {
    dashboard,
    siteDetails,
    dateRange,
    availableDates,
    recentCollections,
    isLoading,
    isCollecting,
    error,
    fetchSiteDetail,
    triggerCollection,
    selectDateRange,
    refreshData,
  } = useNginxAnalytics(accessToken);

  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Convert availableDates strings to Date objects for calendar highlighting
  const highlightedDates = useMemo(() => {
    return availableDates.map((d) => new Date(d + 'T12:00:00'));
  }, [availableDates]);

  // Convert hook's dateRange (string) to DateRangeValue (Date) for the picker
  const pickerDateRange: DateRangeValue | undefined = useMemo(() => {
    if (!dateRange) return undefined;
    return {
      from: new Date(dateRange.from + 'T12:00:00'),
      to: new Date(dateRange.to + 'T12:00:00'),
    };
  }, [dateRange]);

  // Compute aggregate error rate and avg response time from site summaries
  const aggregateStats = useMemo(() => {
    if (!dashboard?.sites || dashboard.sites.length === 0) return { avgErrorRate: undefined, avgResponseTime: null };
    const sitesWithErrorRate = dashboard.sites.filter((s) => s.errorRate !== undefined);
    const avgErrorRate =
      sitesWithErrorRate.length > 0
        ? sitesWithErrorRate.reduce((sum, s) => sum + (s.errorRate || 0), 0) / sitesWithErrorRate.length
        : undefined;
    const sitesWithResponseTime = dashboard.sites.filter(
      (s) => s.avgResponseTime !== null && s.avgResponseTime !== undefined
    );
    const avgResponseTime =
      sitesWithResponseTime.length > 0
        ? sitesWithResponseTime.reduce((sum, s) => sum + (s.avgResponseTime || 0), 0) /
          sitesWithResponseTime.length
        : null;
    return { avgErrorRate, avgResponseTime };
  }, [dashboard]);

  // Auto-select first site when dashboard loads
  useEffect(() => {
    if (dashboard?.sites && dashboard.sites.length > 0 && selectedSiteId === null) {
      setSelectedSiteId(dashboard.sites[0].siteId);
    }
  }, [dashboard, selectedSiteId]);

  // Fetch detail for selected site when selection or date changes
  const loadSiteDetail = useCallback(
    async (siteId: number) => {
      if (!dateRange) return;
      setIsLoadingDetail(true);
      try {
        await fetchSiteDetail(siteId, dateRange.from, dateRange.to);
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [fetchSiteDetail, dateRange]
  );

  useEffect(() => {
    if (selectedSiteId !== null && dateRange) {
      const alreadyLoaded = siteDetails.has(selectedSiteId);
      if (!alreadyLoaded) {
        loadSiteDetail(selectedSiteId);
      }
    }
  }, [selectedSiteId, dateRange, siteDetails, loadSiteDetail]);

  const handleSelectSite = useCallback(
    (siteId: number) => {
      setSelectedSiteId(siteId);
      if (dateRange && !siteDetails.has(siteId)) {
        loadSiteDetail(siteId);
      }
    },
    [dateRange, siteDetails, loadSiteDetail]
  );

  const handleRefresh = useCallback(async () => {
    await triggerCollection();
    setTimeout(() => { refreshData(); }, 3000);
  }, [triggerCollection, refreshData]);

  const handleDateRangeChange = (range: DateRangeValue | undefined) => {
    if (range?.from) {
      const from = format(range.from, 'yyyy-MM-dd');
      const to = range.to ? format(range.to, 'yyyy-MM-dd') : from;
      selectDateRange({ from, to });
      setSelectedSiteId(null);
    }
  };

  const latestCollection = recentCollections[0];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Carregando dados do VPS...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-destructive font-medium mb-4">{error}</p>
              <Button onClick={refreshData}>Tentar Novamente</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!dashboard || !dashboard.sites) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              Nenhum dado disponível para a data selecionada.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedSiteDetail =
    selectedSiteId !== null ? siteDetails.get(selectedSiteId) : undefined;

  const selectedSiteSummary =
    selectedSiteId !== null
      ? dashboard.sites.find((s) => s.siteId === selectedSiteId)
      : undefined;

  return (
    <NginxErrorBoundary>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Dashboard VPS</h1>
            <p className="text-muted-foreground">Análise de tráfego dos servidores nginx</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <DateRangePicker
              dateRange={pickerDateRange}
              onDateRangeChange={handleDateRangeChange}
              placeholder="Selecionar período"
              highlightedDates={highlightedDates}
            />

            <Button onClick={handleRefresh} disabled={isCollecting}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isCollecting ? 'animate-spin' : ''}`} />
              {isCollecting ? 'Coletando...' : 'Atualizar Agora'}
            </Button>
          </div>
        </div>

        {/* Last collection info */}
        {latestCollection && (
          <div className="text-sm text-muted-foreground">
            Última coleta:{' '}
            {new Date(latestCollection.startedAt).toLocaleString('pt-BR', {
              timeZone: 'America/Sao_Paulo',
            })}{' '}
            -{' '}
            {latestCollection.status === 'COMPLETED'
              ? `${latestCollection.totalEntriesStored.toLocaleString('pt-BR')} registros processados`
              : latestCollection.status}
          </div>
        )}

        {/* Global Summary KPIs */}
        <SummaryKpiCards
          totalRequests={dashboard.totalRequests}
          totalVisitors={dashboard.totalVisitors}
          totalPageViews={dashboard.totalPageViews}
          avgErrorRate={aggregateStats.avgErrorRate}
          avgResponseTime={aggregateStats.avgResponseTime}
        />

        {/* Site Selector Tabs */}
        <div className="space-y-4">
          <SiteTabs
            sites={dashboard.sites}
            selectedSiteId={selectedSiteId}
            onSelectSite={handleSelectSite}
          />

          {/* Selected Site Detail */}
          {selectedSiteId === null ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  Selecione um site acima para ver os detalhes.
                </div>
              </CardContent>
            </Card>
          ) : isLoadingDetail && !selectedSiteDetail ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Carregando detalhes de{' '}
                  <span className="font-medium" style={{ color: selectedSiteSummary?.color }}>
                    {selectedSiteSummary?.name}
                  </span>
                  ...
                </p>
              </div>
            </div>
          ) : selectedSiteDetail && accessToken ? (
            <SiteDetailView
              siteDetail={selectedSiteDetail}
              dateFrom={dateRange?.from || ''}
              dateTo={dateRange?.to || ''}
              accessToken={accessToken}
            />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  Nenhum detalhe disponível para este site na data selecionada.
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </NginxErrorBoundary>
  );
}
